import React, { useEffect, useRef } from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { 
  TrendingUp,
  AccessTime,
  Star,
  CheckCircle,
  AttachMoney,
  Groups,
  AutoAwesome,
  Speed,
  Schedule,
  Savings
} from '@mui/icons-material';

interface TickerItem {
  icon: React.ReactNode;
  text: string;
  highlight?: string;
}

const tickerItems: TickerItem[] = [
  {
    icon: <Star sx={{ fontSize: 16, color: '#FFD700' }} />,
    text: 'Jackie Soto, eHomes: "Game changer for seller leads"',
    highlight: '⭐⭐⭐⭐⭐'
  },
  {
    icon: <Speed sx={{ fontSize: 16, color: '#4CAF50' }} />,
    text: '3.5 second response time',
    highlight: '280% MORE CONVERSIONS'
  },
  {
    icon: <Savings sx={{ fontSize: 16, color: '#00C853' }} />,
    text: 'Save 15-20 hours per week',
    highlight: 'LESS ADMIN'
  },
  {
    icon: <TrendingUp sx={{ fontSize: 16, color: '#2196F3' }} />,
    text: '87% of leads go unanswered after 5 min',
    highlight: 'OPPY RESPONDS'
  },
  {
    icon: <CheckCircle sx={{ fontSize: 16, color: '#4CAF50' }} />,
    text: 'Derek Taylor, T3 Sixty: "Oppy stands out"',
    highlight: 'INDUSTRY LEADER'
  },
  {
    icon: <Schedule sx={{ fontSize: 16, color: '#FF5722' }} />,
    text: '67% of leads come after hours',
    highlight: '24/7 CAPTURE'
  },
  {
    icon: <AttachMoney sx={{ fontSize: 16, color: '#00C853' }} />,
    text: '2.5X conversion boost',
    highlight: 'MORE CLOSINGS'
  },
  {
    icon: <AutoAwesome sx={{ fontSize: 16, color: '#9C27B0' }} />,
    text: 'Tyler Brenner: "Better quality leads"',
    highlight: 'METRO BROKERS'
  },
  {
    icon: <Groups sx={{ fontSize: 16, color: '#2196F3' }} />,
    text: 'Human in the Loop collaboration',
    highlight: 'STAY IN CONTROL'
  },
  {
    icon: <Star sx={{ fontSize: 16, color: '#FFD700' }} />,
    text: 'Qualify leads for pennies on the dollar',
    highlight: 'LOWER COSTS'
  },
  {
    icon: <Speed sx={{ fontSize: 16, color: '#FF5722' }} />,
    text: 'Stop texting on Saturdays',
    highlight: 'GET YOUR LIFE BACK'
  },
  {
    icon: <CheckCircle sx={{ fontSize: 16, color: '#4CAF50' }} />,
    text: '5 min setup • No tech skills needed',
    highlight: 'EASY START'
  },
  {
    icon: <TrendingUp sx={{ fontSize: 16, color: '#2196F3' }} />,
    text: 'Reignite cold leads automatically',
    highlight: 'FIND GOLD'
  },
  {
    icon: <AutoAwesome sx={{ fontSize: 16, color: '#9C27B0' }} />,
    text: 'QR codes turn drive-bys into leads',
    highlight: 'SMART CAPTURE'
  },
  {
    icon: <AttachMoney sx={{ fontSize: 16, color: '#00C853' }} />,
    text: 'Multi-language support included',
    highlight: 'EXPAND REACH'
  }
];

const TickerSlider: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const tickerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>();

  useEffect(() => {
    const ticker = tickerRef.current;
    if (!ticker) return;

    let scrollPos = 0;
    const scrollSpeed = 1.2; // Pixels per frame

    const animate = () => {
      if (!ticker) return;
      
      scrollPos += scrollSpeed;
      
      // Reset when we've scrolled half the content (for seamless loop)
      const maxScroll = ticker.scrollWidth / 2;
      if (scrollPos >= maxScroll) {
        scrollPos = 0;
      }
      
      ticker.scrollLeft = scrollPos;
      animationRef.current = requestAnimationFrame(animate);
    };

    // Start animation
    animationRef.current = requestAnimationFrame(animate);

    // Pause on hover
    const handleMouseEnter = () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };

    const handleMouseLeave = () => {
      scrollPos = ticker.scrollLeft;
      animationRef.current = requestAnimationFrame(animate);
    };

    ticker.addEventListener('mouseenter', handleMouseEnter);
    ticker.addEventListener('touchstart', handleMouseEnter);
    ticker.addEventListener('mouseleave', handleMouseLeave);
    ticker.addEventListener('touchend', handleMouseLeave);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      ticker?.removeEventListener('mouseenter', handleMouseEnter);
      ticker?.removeEventListener('touchstart', handleMouseEnter);
      ticker?.removeEventListener('mouseleave', handleMouseLeave);
      ticker?.removeEventListener('touchend', handleMouseLeave);
    };
  }, []);

  // Double the items for seamless loop
  const displayItems = [...tickerItems, ...tickerItems];

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: isMobile ? '60px' : '48px',
        overflow: 'hidden',
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, rgba(146, 209, 81, 0.1) 0%, rgba(146, 209, 81, 0.05) 100%)'
          : 'linear-gradient(135deg, rgba(146, 209, 81, 0.08) 0%, rgba(146, 209, 81, 0.04) 100%)',
        borderBottom: !isMobile ? `1px solid ${theme.palette.divider}` : 'none',
        borderRadius: !isMobile ? '8px 8px 0 0' : 0,
        display: 'flex',
        alignItems: 'center',
        mx: isMobile ? 0 : -4,
        px: isMobile ? 0 : 4,
        mb: isMobile ? 0 : 2,
      }}
    >
      {/* Gradient edges for fade effect */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: isMobile ? '20px' : '40px',
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(to right, rgba(33, 33, 33, 0.9), transparent)'
            : 'linear-gradient(to right, rgba(255, 255, 255, 0.9), transparent)',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: isMobile ? '20px' : '40px',
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(to left, rgba(33, 33, 33, 0.9), transparent)'
            : 'linear-gradient(to left, rgba(255, 255, 255, 0.9), transparent)',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />

      {/* Ticker content */}
      <Box
        ref={tickerRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
        }}
      >
        {displayItems.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 'fit-content',
            }}
          >
            {item.icon}
            <Typography
              component="span"
              sx={{
                fontSize: '0.9rem',
                fontWeight: 500,
                color: theme.palette.text.primary,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {item.text}
              {item.highlight && (
                <Box
                  component="span"
                  sx={{
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 0.5,
                    background: 'linear-gradient(135deg, #92D151 0%, #7AB544 100%)',
                    color: 'white',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    ml: 0.5,
                    letterSpacing: 0.5,
                  }}
                >
                  {item.highlight}
                </Box>
              )}
            </Typography>
            <Box
              sx={{
                width: 3,
                height: 3,
                borderRadius: '50%',
                backgroundColor: '#92D151',
                opacity: 0.6,
                mx: 0.5,
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default TickerSlider;